import { Component, computed, effect, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FilterInput } from '@it2go/data-grid';
import { TableColumn, TableColumnType } from '../../../../shared/components/kit/table/table.types';
import { Router } from '@angular/router';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { WorkGridItemObject, WorkPerformedProcedureObject } from '@it2go/types';
import { FolderDoctorRecordsPageStore } from './folder-doctor-records-page.store';
import {
    dayDateFromColumn,
    filesColumn,
    performedProceduresColumn,
    timeFromColumn,
    usersChipColumn,
} from '../../../../shared/components/kit/table/table.fields';
import { WorkDataDoctorRecordObject } from '@libs/types/src/services/object-type/work/work-doctor-record.object';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { workUri } from 'packages/client/src/app/utils/router.utils';
import { isAllowed, routeParams } from '../../../../shared/shared.global-service';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { contentGlobalSuccess } from '../../../content.error';
import { workVisitStateColor, workVisitStateEnumOptions } from '@libs/types/src/common/enum/work-visit-state.enum';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { TagComponent } from '../../../../../components/kit/tag/tag.component';
import { Chip } from '@openng/optimus-ui/chip';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import { WorkPrintService } from '../../../../../components/medical-service/work/work-print.service';

@Component({
    selector: 'app-folder-doctor-records-page',
    templateUrl: './folder-doctor-records-page.component.html',
    styleUrl: './folder-doctor-records-page.component.scss',
    providers: [FolderDoctorRecordsPageStore, DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        TableComponent,
        PrimeTemplate,
        TagComponent,
        Chip,
        Tooltip,
    ],
})
export class FolderDoctorRecordsPageComponent {
    protected readonly store = inject(FolderDoctorRecordsPageStore);
    private readonly router = inject(Router);
    private readonly confirm = inject(ConfirmBuilder);
    private readonly translation = inject(TranslateService);
    private readonly datePipe = inject(DatePipe);
    private readonly workPrint = inject(WorkPrintService);

    @ViewChild('doctorRecordTable') doctorRecordTable!: TableComponent;

    protected readonly folderStore = inject(FolderGlobalStore);

    folderId: number = 0;

    protected additionalFilters = computed<FilterInput[][]>(() => {
        return [
            [
                {
                    value: [
                        routeParams.folderId(),
                        ...(this.store.doctorRecordSharingIds() || []).map(String),
                    ],
                    column: 'folderId',
                    operator: 'IN',
                },
            ],
            [
                {
                    value: [WorkTypeEnum.DOCTOR_RECORD],
                    column: 'workType',
                    operator: 'EQ',
                },
            ],
        ];
    });

    protected columns: TableColumn[] = [
        {
            name: 'export',
            exportAs: null,
        },
        dayDateFromColumn,
        {
            name: 'date',
            type: TableColumnType.Date,
            dataKey: 'dateFrom',
        },
        timeFromColumn,
        {
            name: 'diagnoses',
            type: TableColumnType.String,
            dataKey: 'performedProcedures',
            data: (procedures: WorkPerformedProcedureObject[]) => procedures.map((procedure) => procedure.diagnosis),
            exportAs: (item: any) => item?.map((it: any) => it?.code)?.filter((it: any) => it)?.join(', ') || '',
        },
        performedProceduresColumn,
        {
            name: 'extendRecord',
            type: TableColumnType.String50,
        },
        {
            name: 'workData.contactType',
            type: TableColumnType.String,
        },
        {
            name: 'workData.state',
            type: TableColumnType.String,
            filterable: {
                type: 'select',
                selectOptions: () => workVisitStateEnumOptions,
            },
            exportAs: (item: any) => this.translation.instant(`workData.common.enum.workVisitState.${item}`),
        },
        filesColumn,
        usersChipColumn,
        {
            name: 'workData.comment',
            type: TableColumnType.String50,
        },
    ];

    protected doctorRecords = computed(() => {
        const work = this.store.data.getWorks();
        const folderId = Number(routeParams.folderId());

        if (work?.items) {
            const newItems = work.items.map((item: WorkGridItemObject) => ({
                ...item,
                extendRecord: this.generateExtendRecord(item),
                __hideDelete: folderId !== item.folderId,
                __hideDetail: folderId !== item.folderId,
            }));
            return {
                ...work,
                items: newItems,
            };
        }
        return work;
    });

    constructor() {
        this.store.onSuccess(
            () => this.doctorRecordTable?.reload(),
            'deleteWork',
        );

        effect(() => {
            this.folderId = Number(routeParams.folderId());
        });
    }

    protected async detail(item: WorkGridItemObject): Promise<void> {
        await this.router.navigateByUrl(workUri(item, item.serviceId));
    }

    protected delete(input: any): void {
        this.confirm.confirm(
            'common.delete',
            null,
            () => this.store.api.deleteWork({ id: input.item.id })
                .then(() => contentGlobalSuccess.next('common.deleted')),
            null,
            'pi-trash',
            input.event.target,
        );
    }

    protected exportRecord(item: WorkGridItemObject): void {
        this.workPrint.openRow(item);
    }

    protected showDoctorRecordsDialog(): void {
        this.workPrint.openBulk({
            selection: this.doctorRecordTable?.selection(),
            supportedWorkTypes: [WorkTypeEnum.DOCTOR_RECORD],
            dateFrom: null,
            dateTo: null,
            showMedicalReport: true,
            folderIds: [
                this.folderId,
                ...(this.store.doctorRecordSharingIds() || []),
            ],
            medicalReportFolderId: this.folderId,
            medicalReportServiceId: this.folderStore.serviceId(),
        });
    }

    private generateExtendRecord(item: WorkGridItemObject): string {
        const workData = item?.workData as WorkDataDoctorRecordObject;
        if (!item || !workData) {
            return '';
        }

        let duration = 0;
        if (item.performedProcedures?.length) {
            item.performedProcedures.forEach((procedure: WorkPerformedProcedureObject) => {
                duration += procedure?.voucherPrescribedMedicalProcedure?.serviceMedicalProcedure?.procedure?.duration || 0;
            });
        }

        const measuredValues = [
            { name: 'systolicPressure', value: workData?.systolicPressure },
            { name: 'diastolicPressure', value: workData?.diastolicPressure },
            { name: 'pulse', value: workData?.pulse },
            { name: 'breath', value: workData?.breath },
            { name: 'glycemia', value: workData?.glycemia },
            { name: 'spo2', value: workData?.spo2 },
            { name: 'pain', value: workData?.pain },
            { name: 'weight', value: workData.weight },
            {
                name: 'pulseByBeats',
                value: workData.pulseByBeats && this.translation.instant('enum.pulseByBeats.' + workData.pulseByBeats),
            },
            {
                name: 'pulseByTouch',
                value: workData.pulseByTouch && this.translation.instant('enum.pulseByTouch.' + workData.pulseByTouch),
            },
        ].filter(field => field.value !== null && field.value !== undefined)
            .map(field => `<li><span>${this.translation.instant('database.workVisits.workData.' + field.name)}: </span><span>${field.value}</span></li>`)
            .join('');

        const fields = [
            { name: 'dekurs', value: workData.note },
            { name: 'medication', value: workData.medication },
            { name: 'distance', value: workData.distance },
            { name: 'duration', value: duration },
            { name: 'sentByDoctor', value: `${item.users[0]?.name} ${item.users[0]?.surname}` },
            { name: 'measuredValues', value: measuredValues.length ? `<ul>${measuredValues}</ul>` : undefined },
        ];


        const html = fields
            .filter(field => field.value !== null && field.value !== undefined)
            .map(field => `<b>${this.translation.instant('folder.doctorRecords.extendedFields.' + field.name)}:</b><br/><p>${field.value}</p>`)
            .join('<br/>');

        const nextVisit = workData.nextVisit
            ? `<b>${this.translation.instant('folder.doctorRecords.extendedFields.nextVisit')}:</b><br/><p style="color:green">${this.datePipe.transform(workData.nextVisit, 'd.M.y HH:mm')}</p>`
            : '';

        return html + nextVisit;
    }

    protected readonly isAllowed = isAllowed;
    protected readonly workVisitStateColor = workVisitStateColor;
}
